import React, { useRef, useState, useEffect, useCallback } from "react";
import Webcam from "react-webcam";
import { FaMicrophone, FaMicrophoneSlash, FaVideo, FaVideoSlash } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { setLoading, setError, setRoom } from "../../store/slices/roomSlice";
import { toggleVideo, toggleAudio } from "../../store/slices/mediaSlice";
import { loginSuccess } from "../../store/slices/authSlice";
import api from "../../utils/api";
import {
  ensureMeetingSessionId,
  getOrCreateMeetingDeviceId,
  hydrateMeetingIdentity,
  persistMeetingAuth,
} from "../../utils/meetingAuth";
import { getMeetingDisplayName } from "../../utils/meetingDisplayName";
import {
  getVoiceAudioConstraintAttempts,
  prepareVoiceTrack,
} from "../../utils/meetingAudio";

// This only selects the correct UI endpoint. The backend still verifies the
// signed invite before creating an authenticated meeting session.
const getInviteMeetingTypeHint = (token) => {
  try {
    const payloadPart = String(token || '').split('.')[1];
    if (!payloadPart) return '';

    const normalized = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
    const payload = JSON.parse(window.atob(padded));
    return String(payload?.meetingType || '').trim().toLowerCase();
  } catch {
    return '';
  }
};

// Toolbar component, re-usable for Meet-style controls
const PreJoinToolbar = ({
  micOn,
  camOn,
  onToggleMic,
  onToggleCam,
  className = "",
}) => (
  <div className={`flex items-center justify-center gap-6 mt-6 ${className}`}>
    <button
      onClick={onToggleMic}
      type="button"
      aria-pressed={micOn}
      aria-label={micOn ? "Microphone on. Mute microphone" : "Muted. Unmute microphone"}
      className={`
        min-w-[92px] h-14 px-3 flex flex-col items-center justify-center gap-0.5
        ${micOn ? "bg-emerald-700" : "bg-red-600"}
        text-white rounded-2xl shadow-lg transition
        focus:outline-none
      `}
      title={micOn ? "Mute microphone" : "Unmute microphone"}
    >
      {micOn ? <FaMicrophone className="w-5 h-5" /> : <FaMicrophoneSlash className="w-5 h-5" />}
      <span className="text-[11px] font-semibold">{micOn ? "Mic on" : "Muted"}</span>
    </button>
    <button
      onClick={onToggleCam}
      type="button"
      aria-pressed={camOn}
      aria-label={camOn ? "Camera on. Turn camera off" : "Camera off. Turn camera on"}
      className={`
        min-w-[92px] h-14 px-3 flex flex-col items-center justify-center gap-0.5
        ${camOn ? "bg-emerald-700" : "bg-red-600"}
        text-white rounded-2xl shadow-lg transition
        focus:outline-none
      `}
      title={camOn ? "Turn off camera" : "Turn on camera"}
    >
      {camOn ? <FaVideo className="w-5 h-5" /> : <FaVideoSlash className="w-5 h-5" />}
      <span className="text-[11px] font-semibold">{camOn ? "Camera on" : "Camera off"}</span>
    </button>
  </div>
);

const JoinRoom = ({ socket }) => {
  const webcamRef = useRef(null);
  const previewAudioStreamRef = useRef(null);
  const [micPreviewReady, setMicPreviewReady] = useState(false);
  const [roomId, setRoomId] = useState("");
  const [username, setUsername] = useState(sessionStorage.getItem("userName") || "");
  const [userNameGet, setUserNameGet] = useState({});
  const [userStatusGet, setUserStatusGet] = useState('waiting');
  const [roomIdNew, setRoomIdNew] = useState("");
  const [isAutoLogging, setIsAutoLogging] = useState(false);
  const [autoLoginError, setAutoLoginError] = useState(null);

  const dispatch = useDispatch();
  const audioEnabled = useSelector((state) => state.media.audioEnabled);
  const videoEnabled = useSelector((state) => state.media.videoEnabled);
  const authUser = useSelector((state) => state.auth.user);
  const currentRoom = useSelector((state) => state.room.currentRoom);
  const { loading, error } = useSelector((state) => state.room);
  const [remoteVideoStatus, setRemoteVideoStatus] = useState({});
  const navigate = useNavigate();
  const location = useLocation();
  const inviteAutoLoginKeyRef = useRef('');


  // Parse URL parameters
  const query = new URLSearchParams(location.search);
  const roomIdGet = query.get("roomIdGet") || query.get("roomId");
  const inviteToken = query.get("invite");

  // Test microphone and camera independently. A missing microphone must not
  // blank the camera preview, and the preview must never play the microphone
  // back through local speakers (the common prejoin echo source).
  useEffect(() => {
    let cancelled = false;
    const stopPreviewAudio = () => {
      previewAudioStreamRef.current?.getTracks?.().forEach((track) => {
        try { track.stop(); } catch {}
      });
      previewAudioStreamRef.current = null;
    };

    stopPreviewAudio();
    setMicPreviewReady(false);
    if (!audioEnabled) return stopPreviewAudio;

    const prepareMicrophone = async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        dispatch(toggleAudio(false));
        toast.warning('Microphone access is not supported in this browser.', {
          toastId: 'prejoin-microphone-unsupported',
        });
        return;
      }

      const deviceId = localStorage.getItem('meeting.audioInputId') || '';
      let lastError = null;
      for (const audio of getVoiceAudioConstraintAttempts(deviceId)) {
        if (cancelled) return;
        let preview = null;
        try {
          preview = await navigator.mediaDevices.getUserMedia({
            audio,
            video: false,
          });
          const track = preview.getAudioTracks()[0];
          if (!track) {
            preview.getTracks().forEach((item) => item.stop());
            continue;
          }
          await prepareVoiceTrack(track);
          if (cancelled) {
            preview.getTracks().forEach((item) => item.stop());
            return;
          }
          previewAudioStreamRef.current = preview;
          setMicPreviewReady(true);
          return;
        } catch (error) {
          preview?.getTracks?.().forEach((item) => {
            try { item.stop(); } catch {}
          });
          if (cancelled) return;
          lastError = error;
          if (error?.name === 'NotAllowedError') break;
        }
      }

      if (!cancelled) {
        dispatch(toggleAudio(false));
        toast.warning(
          lastError?.name === 'NotAllowedError'
            ? 'Microphone permission was denied. You can join muted.'
            : 'Microphone is unavailable. You can join muted.',
          { toastId: 'prejoin-microphone-unavailable' }
        );
      }
    };

    void prepareMicrophone();
    return () => {
      cancelled = true;
      stopPreviewAudio();
    };
  }, [audioEnabled, dispatch]);

  // ============ AUTO-LOGIN WITH EMAIL ============
  useEffect(() => {
    const performAutoLogin = async () => {
      const hasSignedInvite = Boolean(roomIdGet && inviteToken);

      // A signed invitation is authoritative even when App initially hydrated
      // stale/shared cached auth. Skipping this exchange can join the room
      // with another tab's user/session and makes MediaSoup reject consume.
      if (!hasSignedInvite && authUser) {
        if (roomIdGet) setRoomId(roomIdGet);
        return;
      }
      
      if (!hasSignedInvite) {
        // Check if token exists in storage
        const token = localStorage.getItem('accessToken');
        if (!token) {
          // No authentication, redirect to login
          if (roomIdGet) {
            navigate(`/login?redirect=/join-room?roomIdGet=${roomIdGet}`);
          }
        }
        return;
      }

      const inviteKey = `${roomIdGet}:${inviteToken}`;
      if (inviteAutoLoginKeyRef.current === inviteKey) return;
      inviteAutoLoginKeyRef.current = inviteKey;
      
      setIsAutoLogging(true);
      setAutoLoginError(null);

      try {
        console.log("🔄 Auto-login with signed invite");
        
        const inviteMeetingType = getInviteMeetingTypeHint(inviteToken);
        const autoLoginEndpoint =
          inviteMeetingType === 'interview'
            ? '/rooms/auto-login-interview'
            : '/rooms/auto-login-meeting';

        const response = await api.post(autoLoginEndpoint, {
          roomId: roomIdGet,
          inviteToken,
        });

        console.log("✅ Auto-login response:", response.data);

        if (response.data.success) {
          const {
            token,
            user,
            sessionId,
            status: userStatus = 'waiting',
            meetingType = 'class',
            access = {},
          } = response.data;

          const authPayload = persistMeetingAuth({
            token,
            user,
            sessionId,
            status: userStatus,
            meetingType,
            access,
          });

          dispatch(loginSuccess(authPayload));
          setUsername(getMeetingDisplayName(user, user.email));
          setRoomId(roomIdGet);
          setUserStatusGet(userStatus);
          // Remove the one-time signed token after exchange. Reload/Back must
          // reuse this tab's established identity instead of creating another
          // participant session.
          navigate(
            `/join-room?roomIdGet=${encodeURIComponent(roomIdGet)}`,
            { replace: true }
          );
        }
      } catch (error) {
        console.error("❌ Auto-login error:", error);
        const errorMsg = error.response?.data?.error || "Auto-login failed. Please login manually.";
        setAutoLoginError(errorMsg);
        // Redirect to login
        setTimeout(() => {
          navigate(`/login?redirect=/join-room?roomIdGet=${roomIdGet}`);
        }, 3000);
      } finally {
        setIsAutoLogging(false);
      }
    };

    performAutoLogin();
  }, [roomIdGet, inviteToken, authUser, dispatch, navigate]);

  // Prefill only from the explicit URL. A stale lastRoomId must never
  // redirect a normal Join Room visit back to the left-meeting screen.
  useEffect(() => {
    if (roomIdGet) {
      setRoomId(roomIdGet);
    }
  }, [roomIdGet]);

  // Fetch usernames after authentication
  useEffect(() => {
    const fetchUsernames = async () => {
      if (!(authUser?._id || authUser?.id)) return;
       
      try {
        const res = await api.get(`/rooms/by-user/${String(authUser?._id || authUser?.id)}`);
        const list = Array.isArray(res?.data?.rooms) ? res.data.rooms : [];

        let tempRoomId = null;
        for (const el of list) {
          if (el?.roomId) {
            tempRoomId = el.roomId;
            break;
          }
        }
        setRoomIdNew(tempRoomId);
        
        const idGet = roomIdGet || roomId || currentRoom?.roomId || localStorage.getItem('roomId') || tempRoomId;
        if (!idGet) return;
         
        const response = await api.get(`/rooms/${idGet}`);
        if (response?.data) {
          const hostId = response.data.host?._id || response.data.host;
          const currentUserId = authUser?._id || authUser?.id;
          const isUserHost = String(hostId) === String(currentUserId);

          if (response.data.participants) {
            const usernames = {};
            for (const els of response.data.participants) {
              if (String(els?.user?.id || els?.user?._id) === String(currentUserId)) {
                setUserStatusGet(isUserHost ? 'admitted' : els?.status || 'waiting');
                sessionStorage.setItem('statusget', isUserHost ? 'admitted' : els?.status || 'waiting');
              }
              usernames[els.sessionId] = getMeetingDisplayName(els, "");
            }
            setUserNameGet(usernames);
          }
        }
      } catch (err) {
        console.error("Fetch usernames error:", err);
      }
    };
    fetchUsernames();
  }, [authUser, roomIdGet, roomId, currentRoom]);

  // --- Google Meet–style pre-join avatars & text ---
  const userNames = Object.values(userNameGet).filter(Boolean);
  const userCount = userNames.length;
  const getInitial = (name) => name ? name.trim().charAt(0).toUpperCase() : "?";
  const avatarColors = ["bg-blue-500", "bg-pink-500", "bg-green-500", "bg-yellow-500", "bg-purple-500"];

  let joinText = "";
  if (userCount === 1) {
    joinText = `${userNames[0]} is in this call`;
  } else if (userCount === 2) {
    joinText = `${userNames[0]} and ${userNames[1]} are in this call`;
  } else if (userCount > 2) {
    joinText = `${userNames[0]}, ${userNames[1]} and ${userCount - 2} others are in this call`;
  }

  // --- Meet-style mic/cam toggle logic ---
  const handleVideoToggle = async () => {
    const newVideoStatus = !videoEnabled;
    if (window.mediaSoupHandler?.toggleVideo) {
      await window.mediaSoupHandler.toggleVideo(newVideoStatus);
    } else {
      dispatch(toggleVideo(newVideoStatus));
    }
  };

  const handleAudioToggle = async () => {
    const newAudioStatus = !audioEnabled;
    if (window.mediaSoupHandler?.toggleAudio) {
      await window.mediaSoupHandler.toggleAudio(newAudioStatus);
    } else {
      dispatch(toggleAudio(newAudioStatus));
    }
  };

  // --- Join button logic ---
  const handleJoin = useCallback(async (event) => {
    event?.preventDefault?.();

    const idGet = String(roomIdGet || roomId || '').trim();
    if (!idGet) return;

    if (!authUser) {
      dispatch(setError('Please login first to join the meeting.'));
      return;
    }

    dispatch(setLoading(true));

    hydrateMeetingIdentity();
    const sessionId = ensureMeetingSessionId();

    const deviceSig = getOrCreateMeetingDeviceId();
    const lockKey = `meet-lock:${idGet}:${deviceSig}`;

    try {
      const rawLock = localStorage.getItem(lockKey);
      const existingLock = rawLock ? JSON.parse(rawLock) : null;
      const lockIsFresh =
        existingLock?.ts && Date.now() - Number(existingLock.ts) < 90_000;
      const sameSession = existingLock?.pid === sessionId;

      if (lockIsFresh && !sameSession) {
        dispatch(setLoading(false));
        dispatch(
          setError("You're already in this meeting in another tab on this device.")
        );
        return;
      }

      if (!lockIsFresh && rawLock) {
        localStorage.removeItem(lockKey);
      }
    } catch {
      // Ignore malformed/stale lock data and continue through backend validation.
      localStorage.removeItem(lockKey);
    }

    sessionStorage.setItem('deviceSig', deviceSig);
    localStorage.setItem('deviceSig', deviceSig);
    sessionStorage.setItem('deviceId', deviceSig);
    localStorage.setItem('deviceId', deviceSig);

    try {
      // Safe backend returns the populated room and current-user access flags.
      const response = await api.post(`/rooms/${idGet}/join`, {
        sessionId,
        deviceId: deviceSig,
      });

      const roomData = response.data;
      const access = roomData?.access || {};
      const currentUserId = String(authUser?._id || authUser?.id || '');

      const self = (roomData?.participants || []).find((participant) => {
        const participantUserId = String(
          participant?.user?._id || participant?.user?.id || participant?.user || ''
        );

        return (
          participantUserId === currentUserId &&
          String(participant?.sessionId || '') === String(sessionId)
        );
      });

      const participantStatus =
        self?.status ||
        (access.isHost || access.isCoHost ? 'admitted' : 'waiting');

      if (participantStatus === 'rejected') {
        throw new Error('Your join request was rejected.');
      }

      sessionStorage.setItem('isHost', String(Boolean(access.isHost)));
      sessionStorage.setItem('isCoHost', String(Boolean(access.isCoHost)));
      sessionStorage.setItem('canAdmit', String(Boolean(access.canAdmit)));
      sessionStorage.setItem(
        'canManageMedia',
        String(Boolean(access.canManageMedia))
      );
      sessionStorage.setItem('meetingType', roomData?.meetingType || 'meeting');
      sessionStorage.setItem('meetingAccess', JSON.stringify(access));
      sessionStorage.setItem('statusget', participantStatus);

      setUserStatusGet(participantStatus);
      dispatch(setRoom(roomData));
      navigate(`/room/${encodeURIComponent(idGet)}`);
    } catch (joinError) {
      console.error('Join error:', joinError);
      dispatch(
        setError(
          joinError.response?.data?.message ||
            joinError.message ||
            'Failed to join room'
        )
      );
    } finally {
      dispatch(setLoading(false));
    }
  }, [authUser, dispatch, navigate, roomId, roomIdGet]);

  // Socket event listeners
  useEffect(() => {
    if (!socket) return;
    socket.on("video-toggle", ({ userId, sessionId, isVideoEnabled }) => {
      setRemoteVideoStatus((prev) => ({
        ...prev,
        [sessionId]: isVideoEnabled,
      }));
    });
    return () => {
      socket.off("video-toggle");
    };
  }, [socket]);

  // Loading state
  if (isAutoLogging) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8fafd]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-indigo-500 mx-auto mb-4"></div>
          <h2 className="text-xl font-medium text-gray-700">Authenticating your session...</h2>
          <p className="text-sm text-gray-500 mt-2">Please wait while we verify your identity</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f8fafd]">
      <div className="flex flex-col md:flex-row w-full max-w-5xl bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-200">
        {/* Left: Video Preview */}
        <div className="md:w-[520px] flex flex-col items-center justify-center bg-[#e9eef6] relative min-h-[330px]">
          <div className="relative w-[94%] max-w-[480px] rounded-2xl shadow-lg overflow-hidden aspect-video bg-black mt-10 mb-5 flex items-center justify-center">
            <div className="absolute top-3 left-3 z-10 text-sm font-semibold bg-black/60 text-white px-3 py-1 rounded-lg shadow">
              {username || "Guest"}
            </div>
            {videoEnabled ? (
              <Webcam
                ref={webcamRef}
                audio={false}
                muted={true}
                className="w-full h-full object-cover"
                videoConstraints={{
                  ...(localStorage.getItem('meeting.videoInputId')
                    ? {
                        deviceId: {
                          ideal: localStorage.getItem('meeting.videoInputId'),
                        },
                      }
                    : {}),
                  facingMode: "user",
                }}
                onUserMediaError={(error) => {
                  dispatch(toggleVideo(false));
                  toast.warning(
                    error?.name === 'NotAllowedError'
                      ? 'Camera permission was denied. You can join with camera off.'
                      : 'Camera is unavailable. You can join with camera off.',
                    { toastId: 'prejoin-camera-unavailable' }
                  );
                }}
                mirrored={true}
              />
            ) : (
              <div className="flex flex-col items-center justify-center w-full h-full bg-gray-900">
                <span className="text-white text-7xl mb-1">
                  {username ? username.charAt(0).toUpperCase() : "?"}
                </span>
                <span className="text-gray-200 font-semibold text-lg">Camera is off</span>
              </div>
            )}
            <div className="absolute top-3 right-3">
              <button className="w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <circle cx="12" cy="6" r="1.5" />
                  <circle cx="12" cy="12" r="1.5" />
                  <circle cx="12" cy="18" r="1.5" />
                </svg>
              </button>
            </div>
          </div>
          
          <PreJoinToolbar
            micOn={audioEnabled}
            camOn={videoEnabled}
            onToggleMic={handleAudioToggle}
            onToggleCam={handleVideoToggle}
          />
          {audioEnabled && !micPreviewReady && (
            <p className="mt-2 text-xs font-medium text-slate-600" role="status">
              Checking microphone…
            </p>
          )}
        </div>

        {/* Right: Join options */}
        <div className="flex flex-col justify-center px-10 py-12 w-full md:w-[350px]">
          <div className="mb-1 text-gray-900 font-semibold text-xl">
            Ready to join?
          </div>

          {autoLoginError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              ⚠️ {autoLoginError}
            </div>
          )}

          {!authUser && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-700 text-sm">
              ⚠️ Please login to join this meeting.
              <button 
                onClick={() => navigate(`/login?redirect=/join-room?roomIdGet=${roomIdGet || roomId}`)}
                className="ml-2 text-blue-600 underline font-medium"
              >
                Login here
              </button>
            </div>
          )}

          {userCount > 0 && (
            <div className="grid items-center gap-3 mb-6">
              <div className="flex -space-x-2">
                {userNames.slice(0, 3).map((name, idx) => (
                  <div
                    key={name}
                    className={`w-8 h-8 rounded-full ${avatarColors[idx % avatarColors.length]} text-white flex items-center justify-center font-semibold border-2 border-white shadow-sm text-base`}
                    title={name}
                    style={{ zIndex: 10 - idx }}
                  >
                    {getInitial(name)}
                  </div>
                ))}
              </div>
              <div className="flex -space-x-2">
                {userCount > 3 && (
                  <div className="w-8 h-8 rounded-full bg-gray-300 text-gray-700 flex items-center justify-center font-semibold border-2 border-white shadow-sm text-base">
                    +{userCount - 3}
                  </div>
                )}
              </div>
              <span className="text-gray-700 text-sm">{joinText}</span>
            </div>
          )}

          {!roomIdGet && (
            <form onSubmit={handleJoin} className="mb-3">
              <label htmlFor="roomId" className="text-xs text-gray-600 block mb-1">
                Meeting ID *
              </label>
              <input
                id="roomId"
                type="text"
                required
                placeholder="e.g. abc-defg-hij"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none mb-3"
                value={roomId}
                onChange={(e) => setRoomId(e.target.value)}
              />
            </form>
          )}

          <button
            onClick={handleJoin}
            disabled={loading || !authUser || (!roomIdGet && !roomId)}
            className={`w-full py-3 mb-2 rounded-lg font-bold text-lg
              ${loading || !authUser || (!roomIdGet && !roomId)
                ? "bg-gray-400 text-white cursor-not-allowed"
                : "bg-[#4338ca] text-white hover:bg-[#3730a3] transition"}`}
          >
            {loading ? "Joining..." : !authUser ? "Please Login" : "Join now"}
          </button>

          {error && (
            <div className="mt-4 p-2 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JoinRoom;
